import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Radio, Pause, Play, Phone, UserX, CheckCircle2, LogIn, Ban, CalendarPlus, TrendingUp, BarChart3, Building2, CalendarClock, CheckCheck, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, apiError, wsUrl } from "@/lib/api";
import { todayAST, monthAST } from "@/lib/date";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import RescheduleDialog from "@/components/RescheduleDialog";
import NewAppointmentDialog from "@/components/NewAppointmentDialog";
import InsurancesEditor from "@/components/InsurancesEditor";
import ShareBookingLink from "@/components/ShareBookingLink";

const today = todayAST;
const thisMonth = monthAST;

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAssistant = user.role === "assistant";
  const doctorId = user.role === "doctor" ? user.user_id : user.doctor_id;
  const date = today();
  const [appts, setAppts] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [center, setCenter] = useState("");
  const [centers, setCenters] = useState([]);
  const [queue, setQueue] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [income, setIncome] = useState([]);
  const [byCenter, setByCenter] = useState([]);
  const [showNewAppt, setShowNewAppt] = useState(false);
  const [reschedAppt, setReschedAppt] = useState(null);
  const [metricCenter, setMetricCenter] = useState("");
  const wsRef = useRef(null);

  const loadAppts = useCallback(async () => {
    const { data } = await api.get("/appointments/doctor", { params: { date } });
    setAppts(data);
    const set = Array.from(new Set(data.map((a) => a.center).filter(Boolean)));
    setCenters(set);
    setCenter((c) => c || set[0] || "");
    return set;
  }, [date]);

  const loadQueue = useCallback(async (c) => {
    if (!c) return;
    const { data } = await api.get("/queue/status", { params: { doctor_id: doctorId, center: c, date } });
    setQueue(data);
  }, [doctorId, date]);

  const loadMetrics = useCallback(async (c) => {
    const { data } = await api.get("/doctor/metrics", { params: { month: thisMonth(), center: c || undefined } });
    setMetrics(data);
  }, []);

  const loadCharts = useCallback(async () => {
    const [inc, bc] = await Promise.all([
      api.get("/doctor/income-history", { params: { months: 6 } }),
      api.get("/doctor/metrics/by-center", { params: { month: thisMonth() } }),
    ]);
    setIncome(inc.data);
    setByCenter(bc.data.centers || []);
  }, []);

  const loadUpcoming = useCallback(async () => {
    const { data } = await api.get("/appointments/doctor");
    const t = today();
    setUpcoming(
      data
        .filter((a) => a.date > t && (a.status === "pending" || a.status === "confirmed"))
        .sort((x, y) => (x.date + (x.slot_time || "")).localeCompare(y.date + (y.slot_time || "")))
    );
  }, []);

  useEffect(() => {
    (async () => {
      const set = await loadAppts();
      const c = set[0] || "";
      if (c) loadQueue(c);
      if (!isAssistant) loadCharts();
      loadUpcoming();
    })();
  }, [loadAppts, loadQueue, loadCharts, loadUpcoming, isAssistant]);

  useEffect(() => {
    if (isAssistant) return;
    loadMetrics(metricCenter);
  }, [metricCenter, isAssistant, loadMetrics]);

  useEffect(() => {
    if (!center) return;
    loadQueue(center);
    const path = `/api/ws/queue/${doctorId}/${encodeURIComponent(center)}/${date}`;
    try {
      const ws = new WebSocket(wsUrl(path));
      wsRef.current = ws;
      ws.onmessage = (e) => {
        try { setQueue(JSON.parse(e.data)); }
        catch (err) { console.error("Mensaje de cola inválido:", err); }
      };
    } catch (e) {
      console.error("No se pudo abrir el WebSocket de la cola:", e);
    }
    return () => { try { wsRef.current && wsRef.current.close(); } catch { /* cierre de limpieza, se ignora */ } };
  }, [center, doctorId, date, loadQueue]);

  const act = async (fn, msg) => {
    try {
      await fn();
      if (msg) toast.success(msg);
      await loadAppts();
      await loadQueue(center);
      if (!isAssistant) await loadMetrics(metricCenter);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const attend = async (a) => {
    try {
      if (a.status !== "in_progress") await api.patch(`/appointments/${a.id}/start`);
      navigate(`/panel/consulta/${a.id}`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const filtered = appts.filter((a) => !center || a.center === center);
  const metricCenters = Array.from(new Set([...(metrics?.centers || []), ...byCenter.map((c) => c.center), ...centers].filter(Boolean)));

  return (
    <div className="relative">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Panel del día</h1>
          <p className="mt-1 text-sm font-semibold text-slate-500">{date}</p>
        </div>
        <div className="flex items-center gap-3">
          {centers.length > 1 && (
            <Select value={center} onValueChange={setCenter}>
              <SelectTrigger className="w-56 rounded-xl border-[#E2E8F0] font-bold" data-testid="center-select">
                <SelectValue placeholder="Centro" />
              </SelectTrigger>
              <SelectContent>
                {centers.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Button onClick={() => setShowNewAppt(true)} className="rounded-full bg-[#0284C7] font-bold hover:bg-[#0369a1]" data-testid="new-appointment-btn">
            <CalendarPlus className="mr-1.5 h-4 w-4" /> Agendar cita
          </Button>
        </div>
      </div>

      {/* Monthly metrics */}
      {!isAssistant && (
      <>
      <div className="mt-6">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-400">
            <TrendingUp className="h-3.5 w-3.5" /> Métricas del mes {metrics?.month} {metricCenter ? `· ${metricCenter}` : "· Todos los centros"}
          </div>
          <Select value={metricCenter || "__all__"} onValueChange={(v) => setMetricCenter(v === "__all__" ? "" : v)}>
            <SelectTrigger className="h-8 w-56 rounded-full border-[#E2E8F0] text-xs font-bold" data-testid="metric-center-select">
              <SelectValue placeholder="Todos los centros" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos los centros</SelectItem>
              {metricCenters.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
          <Metric label="Citas" value={metrics?.total} testid="m-total" />
          <Metric label="Completadas" value={metrics?.completed} testid="m-completed" />
          <Metric label="Canceladas" value={metrics?.cancelled} testid="m-cancelled" />
          <Metric label="Pacientes" value={metrics?.unique_patients} testid="m-patients" />
          <Metric label="Ingresos RD$" value={metrics ? metrics.revenue?.toLocaleString() : undefined} testid="m-revenue" accent />
        </div>
      </div>

      {/* Income history chart + per-center breakdown */}
      <div className="mt-6 grid gap-6 lg:grid-cols-5">
        <div className="lg:col-span-3 rounded-2xl border border-[#E2E8F0] bg-white p-5">
          <div className="mb-4 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-400">
            <BarChart3 className="h-3.5 w-3.5" /> Historial de ingresos (últimos 6 meses)
          </div>
          <IncomeChart data={income} />
        </div>
        <div className="lg:col-span-2 rounded-2xl border border-[#E2E8F0] bg-white p-5">
          <div className="mb-4 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-400">
            <Building2 className="h-3.5 w-3.5" /> Métricas por centro · {metrics?.month}
          </div>
          <div className="space-y-3">
            {byCenter.length === 0 && <p className="text-sm font-bold text-slate-400">Sin datos este mes.</p>}
            {byCenter.map((c) => (
              <div key={c.center} className="rounded-xl border border-[#E2E8F0] p-3" data-testid={`bycenter-${c.center}`}>
                <div className="text-sm font-extrabold text-slate-900">{c.center}</div>
                <div className="mt-2 grid grid-cols-4 gap-2 text-center">
                  <MiniStat label="Citas" value={c.total} />
                  <MiniStat label="Compl." value={c.completed} />
                  <MiniStat label="Cancel." value={c.cancelled} />
                  <MiniStat label="RD$" value={c.revenue?.toLocaleString()} accent />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      </>
      )}

      <div className="mt-6"><ShareBookingLink doctorId={doctorId} /></div>
      {isAssistant && <div className="mt-6"><InsurancesEditor /></div>}

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        {/* Live turn monitor */}
        <div className="lg:col-span-1">
          <div className="rounded-2xl border border-sky-200 bg-white p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#0284C7]">
                <Radio className="h-4 w-4 animate-pulse" /> Turnos en vivo
              </div>
              {queue && (
                <Button size="sm" variant="outline" onClick={() => act(() => api.post("/queue/pause", null, { params: { center, date } }), queue.paused ? "Cola reanudada" : "Cola en pausa")} className="rounded-full border-[#E2E8F0] font-bold" data-testid="pause-queue-btn">
                  {queue.paused ? <><Play className="mr-1 h-3.5 w-3.5" /> Reanudar</> : <><Pause className="mr-1 h-3.5 w-3.5" /> Pausar</>}
                </Button>
              )}
            </div>
            {(queue?.queue || []).length === 0 ? (
              <div className="mt-5 rounded-2xl bg-slate-50 p-6 text-center" data-testid="dash-no-pending">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Turno actual</div>
                <div className="mt-2 text-lg font-extrabold text-slate-500">No hay turnos pendientes</div>
              </div>
            ) : (
              <div className="mt-5 rounded-2xl bg-sky-50 p-6 text-center">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Turno actual</div>
                <div className="mt-1 text-6xl font-extrabold text-[#0284C7] animate-pulse" data-testid="dash-current-turn">{queue.current_turn || "—"}</div>
                <div className="mt-2 text-sm font-bold text-slate-500" data-testid="dash-waiting-count">{queue.waiting_count || 0} en espera</div>
              </div>
            )}
            <div className="mt-5 space-y-2">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Cola</div>
              {(queue?.queue || []).length === 0 && <p className="text-sm font-bold text-slate-400">Nadie en cola.</p>}
              {(queue?.queue || []).map((q) => (
                <div key={q.appointment_id} className={`flex items-center justify-between rounded-xl border px-3 py-2 ${q.status === "in_progress" ? "border-[#0284C7] bg-sky-50" : "border-[#E2E8F0]"}`} data-testid={`queue-item-${q.appointment_id}`}>
                  <div className="flex items-center gap-3">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-xs font-extrabold text-white">{q.turn_number}</span>
                    <span className="text-sm font-bold text-slate-700">{q.patient_name}</span>
                    {q.is_walk_in && <WalkInBadge />}
                  </div>
                  {q.status === "arrived" && (
                    <button onClick={() => act(() => api.patch(`/appointments/${q.appointment_id}/noshow`), "Enviado al final")} className="rounded-full bg-amber-500 p-1.5 text-white" title="No asistió" data-testid={`noshow-${q.appointment_id}`}><UserX className="h-3.5 w-3.5" /></button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Appointments table */}
        <div className="lg:col-span-2">
          <div className="overflow-hidden rounded-2xl border border-[#E2E8F0] bg-white">
            <div className="border-b border-[#E2E8F0] px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Citas del día</div>
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#E2E8F0] text-xs font-bold uppercase tracking-wider text-slate-400">
                  <th className="px-5 py-3">Paciente</th>
                  <th className="px-5 py-3">Modalidad</th>
                  <th className="px-5 py-3">Estado</th>
                  <th className="px-5 py-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && <tr><td colSpan={4} className="px-5 py-10 text-center text-sm font-bold text-slate-400">Sin citas para hoy.</td></tr>}
                {filtered.map((a) => (
                  <tr key={a.id} className="border-b border-[#E2E8F0] last:border-0 hover:bg-slate-50" data-testid={`row-${a.id}`}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-extrabold text-slate-900">{a.patient_name}</span>
                        {a.is_walk_in && <WalkInBadge />}
                      </div>
                      <div className="text-xs font-bold text-slate-400">{a.turn_number ? `Turno ${a.turn_number}` : a.slot_time || "—"}</div>
                    </td>
                    <td className="px-5 py-3 text-sm font-bold text-slate-500">{a.modality === "HORAS" ? "Hora fija" : "Por llegada"}</td>
                    <td className="px-5 py-3"><StatusBadge status={a.status} testid={`row-status-${a.id}`} /></td>
                    <td className="px-5 py-3">
                      <div className="flex flex-wrap justify-end gap-1.5">
                        {a.status === "pending" && (
                          <Button size="sm" variant="outline" onClick={() => act(() => api.patch(`/appointments/${a.id}/confirm`), "Cita confirmada")} className="rounded-full border-[#E2E8F0] font-bold text-emerald-600" data-testid={`confirm-${a.id}`}>
                            <CheckCheck className="mr-1 h-3.5 w-3.5" /> Confirmar
                          </Button>
                        )}
                        {(a.status === "pending" || a.status === "confirmed") && (
                          <Button size="sm" variant="outline" onClick={() => setReschedAppt(a)} className="rounded-full border-[#E2E8F0] font-bold text-slate-600" data-testid={`reschedule-${a.id}`}>
                            <CalendarClock className="mr-1 h-3.5 w-3.5" /> Reprogramar
                          </Button>
                        )}
                        {(a.status === "pending" || a.status === "confirmed") && (
                          <Button size="sm" onClick={() => act(() => api.patch(`/appointments/${a.id}/checkin`), "Llegada registrada")} className="rounded-full bg-[#0284C7] font-bold hover:bg-[#0369a1]" data-testid={`checkin-${a.id}`}>
                            <LogIn className="mr-1 h-3.5 w-3.5" /> Registrar llegada
                          </Button>
                        )}
                        {(a.status === "arrived" || a.status === "in_progress") && !isAssistant && (
                          <Button size="sm" onClick={() => attend(a)} className="rounded-full bg-emerald-500 font-bold hover:bg-emerald-600" data-testid={`attend-${a.id}`}>
                            <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Atender consulta
                          </Button>
                        )}
                        {a.status === "pending" && (
                          <Button size="sm" variant="outline" onClick={() => act(() => api.patch(`/appointments/${a.id}/cancel`), "Cancelada")} className="rounded-full border-[#E2E8F0] font-bold text-red-500" data-testid={`cancel-${a.id}`}>
                            <Ban className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Próximas citas */}
      <div className="mt-6 overflow-hidden rounded-2xl border border-[#E2E8F0] bg-white">
        <div className="border-b border-[#E2E8F0] px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Próximas citas</div>
        <table className="w-full text-left" data-testid="upcoming-table">
          <thead>
            <tr className="border-b border-[#E2E8F0] text-xs font-bold uppercase tracking-wider text-slate-400">
              <th className="px-5 py-3">Paciente</th>
              <th className="px-5 py-3">Fecha</th>
              <th className="px-5 py-3">Centro</th>
              <th className="px-5 py-3">Modalidad</th>
              <th className="px-5 py-3">Estado</th>
            </tr>
          </thead>
          <tbody>
            {upcoming.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-sm font-bold text-slate-400">No hay próximas citas.</td></tr>}
            {upcoming.map((a) => (
              <tr key={a.id} className="border-b border-[#E2E8F0] last:border-0 hover:bg-slate-50" data-testid={`upcoming-row-${a.id}`}>
                <td className="px-5 py-3 text-sm font-extrabold text-slate-900">{a.patient_name}</td>
                <td className="px-5 py-3 text-sm font-bold text-slate-500">{a.date}{a.slot_time ? ` · ${a.slot_time}` : ""}</td>
                <td className="px-5 py-3 text-sm font-bold text-slate-500">{a.center}</td>
                <td className="px-5 py-3 text-sm font-bold text-slate-500">{a.modality === "HORAS" ? "Hora fija" : "Por llegada"}</td>
                <td className="px-5 py-3"><StatusBadge status={a.status} testid={`upcoming-status-${a.id}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Floating availability button */}
      {!isAssistant && (
      <Link to="/panel/disponibilidad" data-testid="fab-availability" className="fixed bottom-6 right-6 z-30 flex items-center gap-2 rounded-full bg-[#0284C7] px-5 py-3.5 text-sm font-bold text-white shadow-lg transition-transform duration-200 hover:scale-105 hover:bg-[#0369a1]">
        <CalendarPlus className="h-5 w-5" /> Disponibilidad
      </Link>
      )}

      <NewAppointmentDialog
        doctorId={doctorId}
        open={showNewAppt}
        onOpenChange={setShowNewAppt}
        onDone={async () => { await loadAppts(); await loadQueue(center); await loadUpcoming(); }}
      />
      {reschedAppt && (
        <RescheduleDialog
          appointment={reschedAppt}
          open={!!reschedAppt}
          onOpenChange={(v) => { if (!v) setReschedAppt(null); }}
          onDone={async () => { setReschedAppt(null); await loadAppts(); await loadQueue(center); await loadUpcoming(); }}
        />
      )}
    </div>
  );
}

function WalkInBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-600" data-testid="walkin-badge">
      <UserPlus className="h-3 w-3" /> Sin cuenta
    </span>
  );
}

function Metric({ label, value, testid, accent }) {
  return (
    <div className={`rounded-2xl border p-4 ${accent ? "border-sky-200 bg-sky-50" : "border-[#E2E8F0] bg-white"}`}>
      <div className={`text-2xl font-extrabold ${accent ? "text-[#0284C7]" : "text-slate-900"}`} data-testid={testid}>{value ?? "—"}</div>
      <div className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</div>
    </div>
  );
}

function MiniStat({ label, value, accent }) {
  return (
    <div>
      <div className={`text-sm font-extrabold ${accent ? "text-[#0284C7]" : "text-slate-900"}`}>{value ?? "—"}</div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</div>
    </div>
  );
}

function IncomeChart({ data }) {
  if (!data || data.length === 0) return <p className="text-sm font-bold text-slate-400">Sin datos.</p>;
  const max = Math.max(...data.map((d) => d.revenue), 1);
  const monthLabel = (m) => {
    const names = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
    return names[parseInt(m.slice(5, 7), 10) - 1];
  };
  return (
    <div className="flex items-end justify-between gap-3" style={{ height: 200 }} data-testid="income-chart">
      {data.map((d) => (
        <div key={d.month} className="flex flex-1 flex-col items-center justify-end" data-testid={`income-bar-${d.month}`}>
          <div className="mb-1 text-[11px] font-bold text-slate-700">{d.revenue >= 1000 ? `${Math.round(d.revenue / 1000)}k` : d.revenue}</div>
          <div
            className="w-full max-w-[42px] rounded-t-lg bg-[#0284C7] transition-all duration-500"
            style={{ height: `${Math.max((d.revenue / max) * 150, 4)}px` }}
            title={`RD$ ${d.revenue.toLocaleString()}`}
          />
          <div className="mt-2 text-xs font-extrabold text-slate-500">{monthLabel(d.month)}</div>
          <div className="text-[10px] font-bold text-slate-400">{d.appointments} citas</div>
        </div>
      ))}
    </div>
  );
}
